"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import Button, { ButtonSize, ButtonVariant } from "@/app/(components)/generics/Button/Button";
import IconArrowRight from "@/app/(components)/generics/Icons/IconArrowRight";
import IconEdit from "@/app/(components)/generics/Icons/IconEdit";
import IconPlus from "@/app/(components)/generics/Icons/IconPlus";
import IconTrash from "@/app/(components)/generics/Icons/IconTrash";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import { Tooltip, TooltipPosition } from "@/app/(components)/generics/Tooltip/Tooltip";
import { deleteInsolePair, getPatientInsolePairs } from "@/app/actions/insole-pair/action";
import { ConfirmStore } from "@/app/stores/ConfirmStore";
import { ModalStore } from "@/app/stores/ModalStore";
import { TKey } from "@/app/translations/TKey";
import { T } from "@/app/translations/Translations";
import { PublicInsolePair } from "@/app/types/PublicInsolePair";
import { PublicPatient } from "@/app/types/PublicPatient";
import { ConfiguratorQuery, RouteSegments, Routes } from "@/app/vars/Routes";
import styles from "./PatientInsoles.module.css";

type Props = { lang: string; patient: PublicPatient };

function formatDate(date: Date, lang: string): string {
  return new Date(date).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export default function PatientInsoles(props: Props) {
  const { lang, patient } = props;

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [pairs, setPairs] = useState<PublicInsolePair[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPairs = useCallback(async () => {
    setLoading(true);
    setPairs(await getPatientInsolePairs(patient.uuid));
    setLoading(false);
  }, [patient.uuid]);

  useEffect(() => {
    fetchPairs();
  }, [fetchPairs]);

  const openAdd = () => {
    ModalStore.getState().openAddInsolePair(lang, patient, fetchPairs);
  };

  const openConfigurator = (pairUuid: string) => {
    router.push(Routes.PROTECTED.CONFIGURATOR(lang, { pair: pairUuid }));
  };

  const openEdit = (pair: PublicInsolePair) => {
    ModalStore.getState().openEditInsolePair(lang, pair, () => {
      fetchPairs();

      // If we just renamed the pair currently open in the configurator, refresh
      // so its name updates there too.
      const onConfigurator = pathname.endsWith(RouteSegments.CONFIGURATOR);
      if (onConfigurator && searchParams.get(ConfiguratorQuery.PAIR) === pair.uuid) {
        router.refresh();
      }
    });
  };

  const handleDelete = (pair: PublicInsolePair) => {
    ConfirmStore.getState().open({
      title: T(TKey.CONFIRM_TITLE, lang) as string,
      message: T(TKey.CONFIRM_DELETE_INSOLE_PAIR, lang) as string,
      confirmLabel: T(TKey.DELETE_INSOLE_PAIR, lang) as string,
      onConfirm: async () => {
        const result = await deleteInsolePair(pair.uuid);
        if (!result.success) {
          return { error: T((result.error as TKey) ?? TKey.ERROR, lang) as string };
        }

        // If we are deleting the pair currently open in the configurator,
        // leave it and go back to the patients page.
        const onConfigurator = pathname.endsWith(RouteSegments.CONFIGURATOR);
        if (onConfigurator && searchParams.get(ConfiguratorQuery.PAIR) === pair.uuid) {
          ModalStore.getState().closeModal();
          router.push(Routes.PROTECTED.PRO.PATIENTS(lang));
          return;
        }

        await fetchPairs();
      },
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Button
          variant={ButtonVariant.PRIMARY}
          size={ButtonSize.SM}
          icon={IconPlus}
          onClick={openAdd}
        >
          {T(TKey.ADD_INSOLE_PAIR, lang)}
        </Button>
      </div>

      {loading && <PointsLoader dark delay />}

      {!loading && pairs.length === 0 && (
        <p className={styles.empty}>{T(TKey.INSOLE_PAIRS_EMPTY, lang)}</p>
      )}

      {!loading && pairs.length > 0 && (
        <div className={styles.list}>
          {pairs.map((pair) => (
            <div key={pair.uuid} className={styles.row}>
              <div className={styles.top}>
                <span className={styles.name}>{pair.name}</span>
                <div className={styles.actions}>
                  <Tooltip content={T(TKey.EDIT_INSOLE_PAIR, lang)} position={TooltipPosition.TOP}>
                    <Button
                      variant={ButtonVariant.GHOST}
                      size={ButtonSize.SM}
                      icon={IconEdit}
                      className={`${styles.hoverBtn} ${styles.editBtn}`}
                      onClick={() => openEdit(pair)}
                    />
                  </Tooltip>
                  <Tooltip
                    content={T(TKey.DELETE_INSOLE_PAIR, lang)}
                    position={TooltipPosition.TOP}
                  >
                    <Button
                      variant={ButtonVariant.GHOST}
                      size={ButtonSize.SM}
                      icon={IconTrash}
                      className={`${styles.hoverBtn} ${styles.trashBtn}`}
                      onClick={() => handleDelete(pair)}
                    />
                  </Tooltip>
                  {!(
                    pathname.endsWith(RouteSegments.CONFIGURATOR) &&
                    searchParams.get(ConfiguratorQuery.PAIR) === pair.uuid
                  ) && (
                    <Tooltip
                      content={T(TKey.OPEN_CONFIGURATOR, lang)}
                      position={TooltipPosition.LEFT}
                    >
                      <Button
                        variant={ButtonVariant.GHOST}
                        size={ButtonSize.SM}
                        icon={IconArrowRight}
                        onClick={() => openConfigurator(pair.uuid)}
                      />
                    </Tooltip>
                  )}
                </div>
              </div>

              {pair.notes && <p className={styles.notes}>{pair.notes}</p>}

              <span className={styles.meta}>
                {T(TKey.CREATED_ON, lang)} {formatDate(pair.createdAt, lang)} ·{" "}
                {T(TKey.UPDATED_ON, lang)} {formatDate(pair.updatedAt, lang)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
